import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/utils/seo";
import {
  getArticlesByCategory,
  getCategoryBySlug,
  sortArticles,
  type Article,
  type SortKey,
} from "@/utils/articles";

const REFERENCE_TIME = new Date("2026-10-09T23:59:59").getTime();
const DAY = 24 * 60 * 60 * 1000;

const timeFilters: { key: string; label: string; days?: number }[] = [
  { key: "all", label: "全部时间" },
  { key: "3d", label: "近 3 天", days: 3 },
  { key: "7d", label: "近 7 天", days: 7 },
  { key: "30d", label: "近 30 天", days: 30 },
];

const heatFilters: { key: string; label: string; min?: number }[] = [
  { key: "all", label: "全部热度" },
  { key: "high", label: "高热度内容", min: 780 },
];

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = getCategoryBySlug(type);

  const [sort, setSort] = useState<SortKey>("latest");
  const [timeKey, setTimeKey] = useState("all");
  const [heatKey, setHeatKey] = useState("all");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  useSeo({
    title: category
      ? `${category.name} - 最新黑料资讯尽在黑料`
      : "分类不存在 - 黑料",
    description: category
      ? `${category.desc} 黑料${category.name}频道持续更新最新娱乐八卦、明星黑料与热点内容。`
      : "未找到对应分类，请返回黑料首页浏览最新娱乐八卦与揭秘资讯。",
    keywords: category
      ? `${category.name},黑料,娱乐八卦,明星黑料,内幕揭秘`
      : "黑料,娱乐八卦,揭秘资讯",
    canonicalPath: `/category/${type ?? ""}`,
  });

  const baseList = useMemo<Article[]>(
    () => (type ? getArticlesByCategory(type) : []),
    [type],
  );

  const filtered = useMemo(() => {
    const timeCfg = timeFilters.find((item) => item.key === timeKey);
    const heatCfg = heatFilters.find((item) => item.key === heatKey);
    let result = baseList;
    if (timeCfg?.days) {
      result = result.filter((item) => {
        const time = new Date(`${item.date}T12:00:00`).getTime();
        return REFERENCE_TIME - time <= timeCfg.days! * DAY;
      });
    }
    if (heatCfg?.min) {
      result = result.filter((item) => item.heat >= heatCfg.min!);
    }
    return sortArticles(result, sort);
  }, [baseList, timeKey, heatKey, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, timeKey, heatKey, sort]);

  const resetFilters = () => {
    setTimeKey("all");
    setHeatKey("all");
    setSort("latest");
  };

  if (!category) {
    return (
      <SiteLayout>
        <div className="px-4 md:px-6 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-5">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="font-heading font-extrabold text-2xl mb-3">没有找到这个分类</h1>
          <p className="text-foreground-600 mb-6">该分类不存在或已被调整，去首页看看最新的黑料内容吧。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <SiteLayout>
      <div className="px-4 md:px-6 py-5 md:py-8">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-400">分类</span>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-900 font-medium">{category.name}</span>
        </nav>

        <div className="relative overflow-hidden rounded-lg bg-foreground-950 text-background-50 px-5 md:px-8 py-7 md:py-9 mb-6 md:mb-8">
          <img
            src={category.image}
            alt={category.name}
            title={`${category.name} - 黑料分类`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/80 to-foreground-950/30" />
          <div className="relative z-10 max-w-2xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-8 rounded-md bg-primary-500 flex items-center justify-center">
                <i className={category.icon} />
              </span>
              <span className="text-xs text-background-300 tracking-widest uppercase">
                {category.short}
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl mb-2">
              {category.name}
            </h1>
            <p className="text-sm text-background-200/85 leading-relaxed">{category.desc}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 md:gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div>
                <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-calendar-line text-primary-500" />
                  发布时间
                </h3>
                <ul className="space-y-1">
                  {timeFilters.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={() => setTimeKey(item.key)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition cursor-pointer ${
                          timeKey === item.key
                            ? "bg-primary-500 text-background-50 font-medium"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500" />
                  热门程度
                </h3>
                <ul className="space-y-1">
                  {heatFilters.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={() => setHeatKey(item.key)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition cursor-pointer ${
                          heatKey === item.key
                            ? "bg-primary-500 text-background-50 font-medium"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="w-full px-3 py-2 rounded-md border border-background-300 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <p className="text-sm text-foreground-600">
                共 <span className="font-bold text-foreground-950">{filtered.length}</span> 篇内容
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFilterOpen((prev) => !prev)}
                  className="lg:hidden inline-flex items-center gap-1 px-3 py-2 rounded-md border border-background-300 text-sm cursor-pointer"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                </button>
                <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-background-100">
                  <button
                    type="button"
                    onClick={() => setSort("latest")}
                    className={`px-3 py-1.5 rounded-full text-xs transition cursor-pointer whitespace-nowrap ${
                      sort === "latest" ? "bg-background-50 font-medium" : "text-foreground-600"
                    }`}
                  >
                    最新优先
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("hot")}
                    className={`px-3 py-1.5 rounded-full text-xs transition cursor-pointer whitespace-nowrap ${
                      sort === "hot" ? "bg-background-50 font-medium" : "text-foreground-600"
                    }`}
                  >
                    热门优先
                  </button>
                </div>
                <div className="hidden sm:flex items-center gap-1 px-1 py-1 rounded-full bg-background-100">
                  <button
                    type="button"
                    aria-label="网格视图"
                    onClick={() => setView("grid")}
                    className={`w-7 h-7 rounded-full flex items-center justify-center transition cursor-pointer ${
                      view === "grid" ? "bg-background-50" : "text-foreground-600"
                    }`}
                  >
                    <i className="ri-grid-fill text-sm" />
                  </button>
                  <button
                    type="button"
                    aria-label="列表视图"
                    onClick={() => setView("list")}
                    className={`w-7 h-7 rounded-full flex items-center justify-center transition cursor-pointer ${
                      view === "list" ? "bg-background-50" : "text-foreground-600"
                    }`}
                  >
                    <i className="ri-list-check text-sm" />
                  </button>
                </div>
              </div>
            </div>

            {filterOpen && (
              <div className="lg:hidden mb-5 p-4 rounded-lg border border-background-200 bg-background-50 space-y-4 animate-fade-in">
                <div>
                  <h3 className="font-heading font-bold text-sm mb-2">发布时间</h3>
                  <div className="flex flex-wrap gap-2">
                    {timeFilters.map((item) => (
                      <button
                        type="button"
                        key={item.key}
                        onClick={() => setTimeKey(item.key)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition cursor-pointer ${
                          timeKey === item.key
                            ? "bg-primary-500 border-primary-500 text-background-50"
                            : "border-background-300 text-foreground-600"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-sm mb-2">热门程度</h3>
                  <div className="flex flex-wrap gap-2">
                    {heatFilters.map((item) => (
                      <button
                        type="button"
                        key={item.key}
                        onClick={() => setHeatKey(item.key)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition cursor-pointer ${
                          heatKey === item.key
                            ? "bg-primary-500 border-primary-500 text-background-50"
                            : "border-background-300 text-foreground-600"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-3 py-2 rounded-md border border-background-300 text-sm w-full cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}

            {shown.length === 0 ? (
              <div className="flex flex-col items-center text-center py-20 border border-dashed border-background-300 rounded-lg">
                <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-4">
                  <i className="ri-inbox-line text-3xl" />
                </span>
                <h3 className="font-heading font-bold text-lg mb-2">暂无符合条件的内容</h3>
                <p className="text-sm text-foreground-500 mb-5">
                  换个筛选条件试试，或者直接查看该分类的全部内容。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              </div>
            ) : view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {shown.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {shown.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="horizontal" />
                ))}
              </div>
            )}

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition cursor-pointer whitespace-nowrap"
                >
                  加载更多内容
                  <i className="ri-arrow-down-line" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}